import { SearchConditionParams } from 'components/SearchSideBar/types';
import { createOpQuery } from 'components/SearchSideBar/logics/createQuery/details';
import { createIncomeQuery } from 'components/SearchSideBar/logics/createQuery/incomeQuery';
import { createIndustryQuery } from 'components/SearchSideBar/logics/createQuery/industryQuery';
import { createOccupationUrlQuery } from 'components/SearchSideBar/logics/createQuery/occupationQuery';
import { createJobOfferKindQuery } from 'components/SearchSideBar/logics/createQuery/details/jobOfferKind';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { createConditionQuery } from 'logics/createConditionQuery';
import { DEFAULT_PARAMS } from '../../../../constants';
import { createKeywordQuery } from '../../logics/createKeywordQuery';

/**
 * createConditionQueryObjectの引数を表すインターフェース
 */
interface ArgumentType {
  /** 選択中の条件を保持するオブジェクト */
  selectedCurrentCondition: SelectedCurrentConditionType;
  /** デフォルトのパラメータ */
  defaultParams: typeof DEFAULT_PARAMS;
  /** fidのクエリストリング */
  fidQuery: string | undefined;
}

/**
 * 選択中の条件からクエリのオブジェクトを生成
 *
 * @param {ArgumentType} params - 引数のオブジェクト
 * @param {SelectedCurrentConditionType} params.selectedCurrentCondition - 選択中の条件を保持するオブジェクト
 * @param {typeof DEFAULT_PARAMS} params.defaultParams - デフォルトのパラメータ
 * @param {string | undefined} params.fidQuery - fidのクエリストリング
 * @returns {SearchConditionParams} 各条件から生成されたクエリオブジェクトを返します。
 */
export const createConditionQueryObject = ({
  selectedCurrentCondition,
  defaultParams,
  fidQuery,
}: ArgumentType): SearchConditionParams => {
  // 各条件のクエリを生成
  const occupationQuery = createOccupationUrlQuery(
    selectedCurrentCondition.searchJobTypeLId,
    selectedCurrentCondition.searchJobTypeMId,
    selectedCurrentCondition.searchJobTypeSId
  );

  const areaQuery = createConditionQuery(selectedCurrentCondition.areaId, 'ar');
  const prefectureQuery = createConditionQuery(
    selectedCurrentCondition.prefectureId,
    'pr'
  );
  const cityQuery = createConditionQuery(selectedCurrentCondition.cityId, 'ci');
  const wardQuery = createConditionQuery(selectedCurrentCondition.wardId, 'wa');
  const routeQuery = createConditionQuery(
    selectedCurrentCondition.routeId,
    'rt'
  );
  const stationQuery = createConditionQuery(
    selectedCurrentCondition.stationId,
    'st'
  );
  const industryQuery = createIndustryQuery(
    selectedCurrentCondition.industryLId,
    selectedCurrentCondition.industrySId
  );
  const optionQuery = createOpQuery({
    employmentStatus: selectedCurrentCondition.employmentStatus,
    isNewArrival: selectedCurrentCondition.isNewArrival,
    isNearDeadline: selectedCurrentCondition.isNearDeadline,
    holidayAndWayOfWorking: selectedCurrentCondition.holidayAndWayOfWorking,
    requirements: selectedCurrentCondition.requirements,
    workplaceEnvironment: selectedCurrentCondition.workplaceEnvironment,
    welfareBenefits: selectedCurrentCondition.welfareBenefits,
    languageSkill: selectedCurrentCondition.languageSkills,
    positions: selectedCurrentCondition.positions,
    employeeAverageAge: selectedCurrentCondition.employeeAverageAge,
  });

  const incomeQuery = createIncomeQuery(
    selectedCurrentCondition.lowerLimitIncome,
    selectedCurrentCondition.upperLimitIncome
  );

  const includeKeywordQuery = createKeywordQuery(
    selectedCurrentCondition.includedKeyword,
    selectedCurrentCondition.includedKeywordCategory,
    true
  );

  const excludeKeywordQuery = createKeywordQuery(
    selectedCurrentCondition.excludedKeyword,
    selectedCurrentCondition.excludedKeywordCategory,
    false
  );

  const employNumQuery = createConditionQuery(
    selectedCurrentCondition.employNum,
    'ne'
  );

  const establishQuery = createConditionQuery(
    selectedCurrentCondition.establish,
    'es'
  );

  const jobOfferKindQuery = createJobOfferKindQuery(
    selectedCurrentCondition?.jobOfferKind,
    defaultParams?.tp
  );

  return {
    fid: `fid=${fidQuery}`,
    oc: occupationQuery,
    ar: areaQuery,
    pr: prefectureQuery,
    ci: cityQuery,
    wa: wardQuery,
    rt: routeQuery,
    st: stationQuery,
    ind: industryQuery,
    op: optionQuery,
    ha: incomeQuery,
    feak: includeKeywordQuery.keyword,
    feakwc: includeKeywordQuery.category,
    feani: excludeKeywordQuery.keyword,
    feaniwc: excludeKeywordQuery.category,
    ne: employNumQuery,
    es: establishQuery,
    ss: defaultParams ? `ss=${defaultParams.ss}` : undefined,
    pic: defaultParams ? `pic=${defaultParams.pic}` : undefined,
    ds: defaultParams ? `ds=${defaultParams.ds}` : undefined,
    tp: jobOfferKindQuery,
    bf: defaultParams ? `bf=${defaultParams.bf}` : undefined,
    fasetFlg: defaultParams ? `fasetFlg=${defaultParams.fasetFlg}` : undefined,
  };
};
